import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import type { ApiPreferences } from "@/lib/settings";

/** GET /api/me. Shape: docs/04-architecture/api-contract.md §6.1. */
export interface Me {
  id: string;
  email: string;
  emailVerified: boolean;
  fullName: string | null;
  timeZone: string;
  roles: { practitioner: boolean; supervisor: boolean };
  /**
   * api-contract §6.1. The API has sent these since the entitlements slice; the type did not
   * carry them, so every screen was blind to the plan. Six flags, one per gated action.
   */
  entitlements: {
    canExport: boolean;
    canWriteEntries: boolean;
    canSubmit: boolean;
    canImport: boolean;
    canCreateTrack: boolean;
    supervisorFeatures: boolean;
  };
  /** Stored preferences (PATCH /api/me/preferences). Defaults filled in by the server.
   * snake_case on purpose: this is a stored blob, not a DTO. */
  preferences?: ApiPreferences;
  /** ISO date the purge runs, or null when no deletion is pending. */
  deletionScheduledFor: string | null;
  version: number;
  /** Set only while an admin is viewing as this user (read-only). */
  impersonation?: { sessionId: string; expiresAt: string } | null;
  /** Feature flags for this user, from the admin console. */
  flags?: Record<string, boolean>;
}

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: ({ signal }) => apiFetch<Me>("/api/me", { signal }),
  });
}
